import { useEffect, useState } from "react";

type StageState<TId> = {
    readonly stageKey: string;
    readonly ids: readonly TId[];
    readonly survivors: readonly TId[];
    readonly isStaging: boolean;
};

type StagedSuggestions<TId> = {
    readonly visibleIds: readonly TId[];
    readonly isStaging: boolean;
};

type Options = {
    /** Identifies the selection the suggestions are for; a change stages the next set. */
    readonly stageKey: string;
    readonly delayMs: number;
};

const sameIds = <TId>(a: readonly TId[], b: readonly TId[]): boolean =>
    a.length === b.length && a.every((id, index) => id === b[index]);

const visibleOf = <TId>({ ids, survivors, isStaging }: StageState<TId>): readonly TId[] =>
    isStaging ? survivors.filter((id) => ids.includes(id)) : ids;

/**
 * Staged reveal of suggestions after the selection changes: the ones still relevant stay in place while
 * a loader stands in for the rest, which appear together after `delayMs`. Changes that keep the
 * selection (a dismissal, late-arriving data) apply immediately.
 */
export const useStagedSuggestions = <TId>(
    ids: readonly TId[],
    { stageKey, delayMs }: Options,
): StagedSuggestions<TId> => {
    const [state, setState] = useState<StageState<TId>>({ stageKey, ids, survivors: ids, isStaging: false });

    let current = state;

    if (state.stageKey !== stageKey) {
        const survivors = visibleOf(state).filter((id) => ids.includes(id));

        current = { stageKey, ids, survivors, isStaging: ids.some((id) => !survivors.includes(id)) };
        setState(current);
    } else if (!sameIds(state.ids, ids)) {
        current = { ...state, ids };
        setState(current);
    }

    const { isStaging } = current;

    useEffect(() => {
        if (!isStaging) {
            return;
        }

        const timeout = setTimeout(() => setState((previous) => ({ ...previous, isStaging: false })), delayMs);

        return () => clearTimeout(timeout);
    }, [isStaging, current.stageKey, delayMs]);

    return { visibleIds: visibleOf(current), isStaging };
};
